import React from 'react';
import { IconLinkedIn, IconMapMarker, IconPaperPlane, IconArrowRight } from './Icons';
import { useLanguage } from '../context/LanguageContext';
import useScrollReveal from '../hooks/useScrollReveal';
import './LinkedInPreview.css';

const IconThumbsUp = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
    <path d="M7 22V9l4-7a1.5 1.5 0 0 1 2.75.83V9H19a2 2 0 0 1 2 2v1.5a2.5 2.5 0 0 1-.5 1.5L17 22H7z"/>
  </svg>
);

const IconComment = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const LinkedInPreview = () => {
  const { t } = useLanguage();
  const posts = [
    { id: 1, content: t.linkedInPreview.post1Content, likes: 42, comments: 8, date: t.linkedInPreview.post1Date },
    { id: 2, content: t.linkedInPreview.post2Content, likes: 67, comments: 15, date: t.linkedInPreview.post2Date },
  ];

  const headerReveal = useScrollReveal({ threshold: 0.15 });
  const contentReveal = useScrollReveal({ threshold: 0.1 });

  return (
    <section className="linkedin-preview-fox" id="linkedin">
      <div className="linkedin-preview-fox__bg-grid"></div>
      <div className="linkedin-preview-fox__bg-orb linkedin-preview-fox__bg-orb--1"></div>
      <div className="linkedin-preview-fox__bg-orb linkedin-preview-fox__bg-orb--2"></div>
      <div className="linkedin-preview-fox__bg-orb linkedin-preview-fox__bg-orb--3"></div>
      
      <div className="linkedin-preview-fox__container">
        <div ref={headerReveal.ref} className={`linkedin-preview-fox__header reveal ${headerReveal.isRevealed ? 'reveal--visible' : ''}`}>
          <div className="linkedin-preview-fox__label"><IconLinkedIn size={22} />{t.linkedInPreview.label}</div>
          <h2 className="linkedin-preview-fox__title">
            {t.linkedInPreview.title} <span className="linkedin-preview-fox__title-gradient">{t.linkedInPreview.titleHighlight}</span>
          </h2>
          <p className="linkedin-preview-fox__subtitle">{t.linkedInPreview.subtitle}</p>
        </div>

        <div ref={contentReveal.ref} className={`linkedin-preview-fox__content reveal ${contentReveal.isRevealed ? 'reveal--visible' : ''}`}>
          <div className="linkedin-preview-fox__profile-card reveal-child">
            <div className="linkedin-preview-fox__profile-banner"></div>
            <div className="linkedin-preview-fox__profile-header">
              <div className="linkedin-preview-fox__profile-image-wrapper">
                <img src="/img/linkedin_pic.jpg" alt="Fox Performance" className="linkedin-preview-fox__profile-image" width={80} height={80} loading="lazy" />
              </div>
              <div className="linkedin-preview-fox__profile-info">
                <h3 className="linkedin-preview-fox__profile-name">{t.linkedInPreview.profileName} <span className="linkedin-preview-fox__profile-name-company">{t.linkedInPreview.profileCompany}</span></h3>
                <p className="linkedin-preview-fox__profile-role">{t.linkedInPreview.profileRole}</p>
                <span className="linkedin-preview-fox__location"><IconMapMarker size={14} />{t.linkedInPreview.location}</span>
              </div>
            </div>
            <div className="linkedin-preview-fox__profile-stats">
              <div className="linkedin-preview-fox__stat">
                <span className="linkedin-preview-fox__stat-value">{t.linkedInPreview.followers}</span>
                <span className="linkedin-preview-fox__stat-label">{t.linkedInPreview.followersLabel}</span>
              </div>
              <div className="linkedin-preview-fox__stat">
                <span className="linkedin-preview-fox__stat-value">{t.linkedInPreview.connections}</span>
                <span className="linkedin-preview-fox__stat-label">{t.linkedInPreview.connectionsLabel}</span>
              </div>
            </div>
            <a href="https://www.linkedin.com/in/wolfgang-niedecken/" target="_blank" rel="noopener noreferrer" className="linkedin-preview-fox__follow-btn">
              <IconLinkedIn size={16} />{t.linkedInPreview.followBtn}
            </a>
            <a href="https://www.linkedin.com/in/wolfgang-niedecken/" target="_blank" rel="noopener noreferrer" className="linkedin-preview-fox__message-btn">
              <IconPaperPlane size={16} />{t.linkedInPreview.messageBtn}
            </a>
          </div>

          <div className="linkedin-preview-fox__feed reveal-child">
            <h4 className="linkedin-preview-fox__feed-title">{t.linkedInPreview.feedTitle}</h4>
            {posts.map((post) => (
              <a key={post.id} href="https://www.linkedin.com/in/wolfgang-niedecken/" target="_blank" rel="noopener noreferrer" className="linkedin-preview-fox__post">
                <div className="linkedin-preview-fox__post-header">
                  <img src="/img/linkedin_pic.jpg" alt="Fox Performance" className="linkedin-preview-fox__post-avatar" width={40} height={40} loading="lazy" />
                  <div className="linkedin-preview-fox__post-meta">
                    <span className="linkedin-preview-fox__post-author">Wolfgang Niedecken</span>
                    <span className="linkedin-preview-fox__post-date">{post.date}</span>
                  </div>
                </div>
                <p className="linkedin-preview-fox__post-content">{post.content}</p>
                <div className="linkedin-preview-fox__post-actions">
                  <span className="linkedin-preview-fox__post-action"><IconThumbsUp size={14} />{post.likes}</span>
                  <span className="linkedin-preview-fox__post-action"><IconComment size={14} />{post.comments}</span>
                </div>
              </a>
            ))}
            <a href="https://www.linkedin.com/in/wolfgang-niedecken/" target="_blank" rel="noopener noreferrer" className="linkedin-preview-fox__see-all">
              {t.linkedInPreview.seeAll}<IconArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LinkedInPreview;
